import { useEffect, useState } from "react"
import AuthenticatedLayout from "@components/layout/Authenticated"
import { Box, Text, HStack, Button, ButtonGroup, MenuItem, VStack, Radio, StackDivider, Input, 
    Textarea, Center, IconButton, useDisclosure, RadioGroup, Checkbox, List, ListItem, Stack, Select
} from "@chakra-ui/react"
import { RiArrowDownLine, RiArrowUpLine, RiArrowRightUpLine, RiUploadCloudFill, RiExpandUpDownLine, RiCloseFill,
    RiAddFill, RiSubtractFill, RiPaintBrushFill, RiPrinterFill, RiFilterFill, RiSparklingFill,
    RiArrowDownSLine
} from "@remixicon/react"
import {ModalAsset, SelectAsset} from "@utils/assets"
import { FieldControl, clearInputErrors } from "@components/form"
import rules from "@utils/rules.json"
import countries from '@utils/countries.json'
import { useUserService } from "@services/index"
import { useValidator } from "@handlers/index"

const AddressForm = ({isOpen, onClose, shipping_address, user, getUser, setItem}) => {

    const { first_name, last_name, address, lga, district, state, country, zip_code, phone } = shipping_address || {}
    
    const [errors, setError] = useState({})

    const [data, setData] = useState({});

    const { createUserAddress, requestErrors, load, response } = useUserService()
    
    const {validate, validationErrors} = useValidator(rules.create_shipping_info)

    //Handles Form Inputs
    const onHandleChange = (name, value) => {

        //Sets Input to State
        setData((prevData) => ({ ...prevData, [name]: value }));

        if(name === 'state'){
            countries?.find(country => country?.name === data?.country)?.states?.map((state) => {
                if(state.name === value){
                    return setData((prevData) => ({ 
                        ...prevData, 
                        lga: state?.lgas?.[0]?.name ?? data?.lga, 
                        district: state?.lgas?.[0]?.districts?.[0]?.name ?? data?.district 
                    }));
                }
            })
        }

        if(name === 'country'){
            countries.map((country) => {
                if(country.name === value){
                    return setData((prevData) => ({ 
                        ...prevData, 
                        state: country.states?.[0]?.name ?? '', 
                        lga: country.states?.[0]?.lgas?.[0]?.name ?? '',
                        district: country?.states?.[0]?.lgas?.[0]?.districts?.[0]?.name ?? '' 
                    }));
                }
            })
        }

        if (name === 'lga') {

            const lga = countries
            ?.find(country => country?.name === data?.country)?.states
            ?.find(state => state?.name === data?.state)?.lgas
            ?.find(lga => lga?.name ===  value)
            
            if (lga) {
                setData((prevData) => ({
                    ...prevData,
                    district: lga?.districts?.[0]?.name ?? ''
                }))
            }
        }

        //Validate Input
        validate(name, value)
    };

    //Add User Data to State
    useEffect(() => {
       if (shipping_address) {
            setData(data => (
                {
                    ...data,
                    first_name, 
                    last_name, 
                    address, 
                    lga,
                    district, 
                    state, 
                    country,
                    zip_code, 
                    phone
                }
            ))
       }
    }, [shipping_address])

    useEffect(() => {
        if (response) {
            setData({})
            onClose()
            setItem(null)
            getUser({username: user?.username})
        }
    }, [response])

    //Adds Validation Errors to Error State
    useEffect(() => {
        setError(validationErrors)
    }, [validationErrors])

    //Adds Request Errors to Error State
    useEffect(() => {
        setError(requestErrors)
    }, [requestErrors])
    

    //Disable Submit Button On Conditions
    const isDisabled = load || Object.keys(data).some(field => (field != 'district' && field != 'phone' && field != 'zip_code') && !data[field] ) || Object.keys(errors).length > 0;

    const submit = () => {
        if (!isDisabled) {
            clearInputErrors(setError)
            createUserAddress({...data, user_id: user?.id, address_id: shipping_address?.id})   
        }
    }

    return (
        
        <ModalAsset
            arrowless
            modalProps={{isOpen, onClose, isCentered: true, size: {base: 'full', lg: 'lg'}, closeOnOverlayClick: false}}
            headerContent={
            <Box>
                <Text className="font-semibold">Shipping Form</Text>
                <Text className="text-xs font-normal text-gray-400">Fill in all required info in the form fields below.</Text>
            </Box>
            }
            bodyContent={
                <Box>
                    
                    <Box className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 gap-4">
                        
                        <FieldControl error={errors?.first_name?.[0]} label={'First Name'}>
                            <Input 
                                size={{base: 'lg', lg: 'md'}}  
                                fontSize={'xs'} 
                                placeholder="Enter first name" 
                                value={data?.first_name}
                                onKeyDown={(e) => e.code === "Enter" && submit()}   
                                onChange={(event) => onHandleChange('first_name', event.target.value)} 
                            />
                        </FieldControl>

                        <FieldControl error={errors?.last_name?.[0]} label={'Last name'}>
                            <Input 
                                size={{base: 'lg', lg: 'md'}}  
                                fontSize={'xs'} 
                                placeholder="Enter last name" 
                                value={data?.last_name}
                                onKeyDown={(e) => e.code === "Enter" && submit()}   
                                onChange={(event) => onHandleChange('last_name', event.target.value)} 
                            />
                        </FieldControl>

                        <FieldControl error={errors?.country?.[0]} label={'Country'}>
                            <SelectAsset 
                                value={data?.country}
                                options={countries?.map(country => ({value: country?.name, label: country?.name}))}
                                isSearch
                                size={'md'}
                                placeholder={'Select Country'}
                                onChange={(selected) => onHandleChange('country', selected)}
                            />
                        </FieldControl>

                        <FieldControl error={errors?.state?.[0]} label={'State/Province'}>
                            <SelectAsset 
                                value={data?.state}
                                options={countries?.find(country => country?.name === data?.country)?.states?.map(state => ({value: state?.name, label: state?.name}))}
                                onChange={(selected) => onHandleChange('state', selected)}
                                isSearch
                                size={'md'}
                                placeholder={'Select State/Province'}
                            />
                        </FieldControl>

                        <FieldControl error={errors?.lga?.[0]} label={'LGA / City'}>

                            {
                                countries
                                ?.find(country => country?.name === data?.country)?.states
                                ?.find(state => state?.name === data?.state)?.lgas?.length ?

                                    <SelectAsset 
                                        value={data?.lga}
                                        options={
                                            countries?.find(country => country?.name === data?.country)
                                            ?.states?.find(state => state?.name === data?.state)
                                            ?.lgas?.map(lga => ({value: lga?.name, label: lga?.name}))
                                        }
                                        onChange={(selected) => onHandleChange('lga', selected)}
                                        isSearch
                                        size={'md'}
                                        placeholder={'Select LGA / City'}
                                    />
                                :

                                <Input 
                                    size={{base: 'lg', lg: 'md'}}  
                                    fontSize={'xs'} 
                                    placeholder="Enter city" 
                                    value={data?.lga}
                                    onChange={(event) => onHandleChange('lga', event.target.value)} 
                                />

                            }
                            
                        </FieldControl>

                        <FieldControl error={errors?.district?.[0]} label={'District (optional)'}>

                            {
                                countries
                                ?.find(country => country?.name === data?.country)?.states
                                ?.find(state => state?.name === data?.state)?.lgas
                                ?.find(lga => lga?.name === data?.lga)?.districts?.length ? 

                                    <SelectAsset 
                                        value={data?.district}
                                        options={
                                            countries?.find(country => country?.name === data?.country)
                                            ?.states?.find(state => state?.name === data?.state)
                                            ?.lgas?.find(lga => lga?.name === data?.lga)
                                            ?.districts?.map(district => ({value: district?.name, label: district?.name}))
                                        }
                                        onChange={(selected) => onHandleChange('district', selected)}
                                        isSearch
                                        size={'md'}
                                        placeholder={'Select District'}
                                    />
                                :

                                <Input 
                                    size={{base: 'lg', lg: 'md'}}  
                                    fontSize={'xs'} 
                                    placeholder="Enter district" 
                                    value={data?.district}
                                    onChange={(event) => onHandleChange('district', event.target.value)} 
                                />

                            }
                            
                        </FieldControl>

                        <FieldControl error={errors?.zip_code?.[0]} label={'Zip Code'}>
                            <Input 
                                size={{base: 'lg', lg: 'md'}}  
                                fontSize={'xs'} 
                                placeholder="Enter zip code" 
                                value={data?.zip_code}
                                onKeyDown={(e) => e.code === "Enter" && submit()}   
                                onChange={(event) => onHandleChange('zip_code', event.target.value)} 
                            />
                        </FieldControl>

                    </Box>

                    <Box className="my-5">
                        <FieldControl label={'Phone Number'}>
                            <Input 
                                size={{base: 'lg', lg: 'md'}}  
                                fontSize={'xs'} 
                                placeholder="Enter phone number" 
                                value={data?.phone}
                                onKeyDown={(e) => e.code === "Enter" && submit()}   
                                onChange={(event) => onHandleChange('phone', event.target.value)} 
                            />
                        </FieldControl>
                    </Box>
                    
                    <Box className="mb-10">
                        <FieldControl error={errors?.phone?.[0]} label={'Address'}>
                            <Textarea 
                                size={{base: 'lg', lg: 'md'}}  
                                fontSize={'xs'} 
                                placeholder="Enter address" 
                                value={data?.address}
                                onKeyDown={(e) => e.code === "Enter" && submit()}   
                                onChange={(event) => onHandleChange('address', event.target.value)} 
                            />
                        </FieldControl>
                    </Box>

                </Box>
            }
            footerContent={
                <ButtonGroup>
                    <Button size={'sm'} onClick={() => {
                        onClose()
                        setData({})
                        setItem(null)
                    }} className="border">Cancel</Button>
                    <Button size={'sm'} onClick={submit} isLoading={load} isDisabled={isDisabled} className="!bg-primary-950 !text-white">
                        Save Changes
                    </Button>
                </ButtonGroup>
            }

        />

    )

}


export default AddressForm